class LabTests extends HTMLElement {
    static #TOOLTIP_SIZE_CLASSES = {
        small: ['md:max-w-[218px]', 'md:text-[13.4px]', 'md:p-3'],
        medium: ['md:max-w-[220px]', 'md:text-[13.4px]', 'md:p-[14px]'],
        large: ['md:max-w-[310px]', 'md:text-[13.4px]', 'md:p-[14px]'],
    };

    static #ALL_TOOLTIP_SIZE_CLASSES = [
        ...new Set(Object.values(LabTests.#TOOLTIP_SIZE_CLASSES).flat()),
    ];

    #resultsPageSize = 5;
    #resultsPageIndex = 0;
    #resultsBuffer = [];
    #pendingCategory = null;

    constructor() {
        super();

        this.currentCategory = null;
        this.onFlavorTabChange = this.onFlavorTabChange.bind(this);
        this.onLabDrawerTrigger = this.onLabDrawerTrigger.bind(this);

        this.data = this.#getData();
        this.currentFlavor = this.#resolveInitialFlavor(this.dataset.flavor);

        this.categoryButtonsContainer = this.querySelector('[id="lab-test-category-button"]')?.parentElement;

        this.categoryButtonTemplate = this.querySelector('#lab-test-category-button');
        this.resultTemplate = this.querySelector('#lab-test-result');
        this.understandingTemplate = this.querySelector('#lab-test-result-understanding');

        this.categoryTitle = this.querySelector('[data-lab-test-category-title]');
        this.categoryDescription = this.querySelector('[data-lab-test-category-description]');
        this.categoryUnit = this.querySelector('[data-lab-test-category-unit]');

        this.resultsContainer = this.resultTemplate.parentElement;
        this.understandingContainer = this.understandingTemplate.parentElement;
        this.understandingSection = this.querySelector('[data-lab-test-understanding-section]');

        this.resultDate = this.querySelector('[data-lab-test-result-date]');
        this.resultDownload = this.querySelector('[data-lab-test-result-download]');
        this.analysisByLogo = this.querySelector('[data-lab-test-result-analysis-by-logo]');
        this.analysisByLogoWrap = this.querySelector('[data-lab-test-analysis-by-wrap]');
        this.isoLogo = this.querySelector('[data-lab-test-result-iso-logo]');
        this.isoLogoWrap = this.querySelector('[data-lab-test-iso-logo-wrap]');
        this.resultSource = this.querySelector('[data-lab-test-result-source]');

        this.resultsPagination = this.querySelector('[data-lab-test-results-pagination]');
        this.resultsPageCurrentEl = this.querySelector('[data-lab-test-results-page-current]');
        this.resultsPageTotalEl = this.querySelector('[data-lab-test-results-page-total]');
        this.resultsPrevBtn = this.querySelector('[data-lab-test-results-prev]');
        this.resultsNextBtn = this.querySelector('[data-lab-test-results-next]');

        this.resultsPrevBtn?.addEventListener('click', () => this.#goResultsPage(-1));
        this.resultsNextBtn?.addEventListener('click', () => this.#goResultsPage(1));
    }

    connectedCallback() {
        this.tabsComponent = this.querySelector('ui-tabs');

        if (this.tabsComponent) {
            this.tabsComponent.addEventListener('ui:tabchange', this.onFlavorTabChange);
        }

        document.addEventListener('click', this.onLabDrawerTrigger, true);

        const flavor = this.currentFlavor || this.#getDefaultFlavor();

        if (this.tabsComponent && flavor && this.#syncFlavorTab(flavor)) return;

        this.#initFlavorContent();
    }

    disconnectedCallback() {
        this.tabsComponent?.removeEventListener('ui:tabchange', this.onFlavorTabChange);
        document.removeEventListener('click', this.onLabDrawerTrigger, true);
    }

    onLabDrawerTrigger(event) {
        const trigger = event.target.closest('[data-drawer-trigger="lab"]');

        if (!trigger) return;

        const categoryKey = trigger.getAttribute('data-lab-test-category')?.trim();

        if (!categoryKey) return;

        this.#pendingCategory = categoryKey;

        const applyPending = () => this.#applyPendingCategory();

        queueMicrotask(applyPending);

        requestAnimationFrame(() => {
            applyPending();
            this.#refreshTabsIndicator();
        });
    }

    onFlavorTabChange(event) {
        const { value } = event.detail;

        if (!value) return;

        this.dataset.flavor = value;
        this.setFlavor(value);
    }

    setFlavor(flavor) {
        if (!this.data[flavor]) return;

        this.currentFlavor = flavor;
        this.dataset.flavor = flavor;

        this.#initFlavorContent();
    }

    #initFlavorContent() {
        this.#renderTabs();

        if (this.#applyPendingCategory()) return;

        const firstCategory = this.#getFlavorCategories()?.[0];

        if (firstCategory) {
            this.setCategory(firstCategory.id);
        }
    }

    #findCategoryByKey(key) {
        const normalized = key?.trim();

        if (!normalized) return null;

        const categories = this.#getFlavorCategories();

        return (
            categories.find(
                (category) =>
                    category.id === normalized ||
                    category.name === normalized ||
                    category.title === normalized ||
                    category.name?.toLowerCase() === normalized.toLowerCase() ||
                    category.title?.toLowerCase() === normalized.toLowerCase()
            ) ?? null
        );
    }

    #applyPendingCategory() {
        if (!this.#pendingCategory) return false;

        const match = this.#findCategoryByKey(this.#pendingCategory);

        if (!match) return false;

        this.setCategory(match.id);
        this.#pendingCategory = null;

        return true;
    }

    #refreshTabsIndicator() {
        if (typeof this.tabsComponent?.updateIndicator === 'function') {
            this.tabsComponent.updateIndicator();
        }
    }

    #getDefaultFlavor() {
        return Object.keys(this.data)[0] || null;
    }

    #syncFlavorTab(flavor) {
        if (!this.tabsComponent || !flavor) return false;

        const tab = [...this.tabsComponent.querySelectorAll('[role="tab"]')].find(
            (item) => item.dataset.value === flavor
        );

        if (!tab || typeof this.tabsComponent.activateTab !== 'function') return false;

        this.tabsComponent.activateTab(tab);
        return true;
    }

    setCategory(categoryId) {
        this.currentCategory = categoryId;

        this.#updateTabStates();

        const category = this.#getCurrentCategory();

        if (!category) return;

        this.#renderCategory(category);
    }

    #getData() {
        const script = this.querySelector('script[type="application/json"][data-lab-tests-data]');

        if (!script) return {};

        try {
            const raw = JSON.parse(script.textContent.trim());
            return this.#normalizeLabTestsData(raw);
        } catch (error) {
            console.error('[lab-tests] Failed to parse lab test data:', error);
            return {};
        }
    }

    #normalizeLabTestsData(raw) {
        if (!raw || typeof raw !== 'object') return {};

        const out = {};

        for (const [flavor, categories] of Object.entries(raw)) {
            if (!Array.isArray(categories)) continue;
            out[flavor] = categories.map((cat, index) => this.#normalizeCategory(cat, index));
        }

        return out;
    }

    #normalizeCategory(raw, index) {
        const labResults = Array.isArray(raw.lab_results) ? raw.lab_results : [];
        const understanding = [];

        labResults.forEach((result) => {
            if (result?.understanding) {
                understanding.push({
                    label: result.name || '',
                    description: result.understanding,
                });
            }
        });

        if (raw.extra_explanation_title || raw.extra_explanation_description) {
            understanding.push({
                label: raw.extra_explanation_title || '',
                description: raw.extra_explanation_description || '',
            });
        }

        const title = raw.category || '';
        const download = raw.report_pdf ? String(raw.report_pdf).trim() : '';

        return {
            id: `cat-${index}`,
            name: title || `Category ${index + 1}`,
            title,
            description: raw.description || '',
            unit: raw.measurement || '',
            results: labResults,
            understanding,
            date: raw.date || '',
            download,
            analysisByLogo: raw.laboratory_logo ? String(raw.laboratory_logo).trim() : null,
            isoLogo: raw.iso_certificate_logo ? String(raw.iso_certificate_logo).trim() : null,
            source: raw.source || '',
        };
    }

    #resolveInitialFlavor(preferred) {
        const key = preferred?.trim();
        if (key && this.data[key]) return key;
        if (this.data.Citrus) return 'Citrus';
        const flavors = Object.keys(this.data);
        return flavors[0] || null;
    }

    #getFlavorCategories() {
        return this.data?.[this.currentFlavor] || [];
    }

    #getCurrentCategory() {
        return this.#getFlavorCategories().find((category) => category.id === this.currentCategory);
    }

    #renderTabs() {
        const buttons = this.categoryButtonsContainer.querySelectorAll('[data-lab-test-category]');

        buttons.forEach((button) => button.remove());

        const categories = this.#getFlavorCategories();

        categories.forEach((category) => {
            const clone = this.categoryButtonTemplate.content.cloneNode(true);

            const button = clone.querySelector('button');

            button.textContent = category.name;
            button.dataset.labTestCategory = category.id;

            button.addEventListener('click', () => {
                this.setCategory(category.id);
            });

            this.categoryButtonsContainer.append(clone);
        });
    }

    #updateTabStates() {
        const buttons = this.querySelectorAll('[data-lab-test-category]');

        buttons.forEach((button) => {
            const isActive = button.dataset.labTestCategory === this.currentCategory;

            button.setAttribute('aria-selected', isActive);

            button.classList.toggle('bg-gray-600', isActive);
            button.classList.toggle('text-gray-100', isActive);
        });
    }

    #renderCategory(category) {
        this.categoryTitle.textContent = category.title || '';
        this.categoryDescription.textContent = category.description || '';
        this.categoryUnit.textContent = category.unit || '';

        this.#resultsBuffer = category.results || [];
        this.#resultsPageIndex = 0;
        this.#renderResultsPage();
        this.#renderUnderstanding(category.understanding || []);

        this.resultDate.textContent = category.date || '';

        if (category.download) {
            this.resultDownload.href = category.download;
            this.resultDownload.removeAttribute('hidden');
        } else {
            this.resultDownload.setAttribute('hidden', '');
        }

        this.#setCategoryLogo(this.analysisByLogo, this.analysisByLogoWrap, category.analysisByLogo);
        this.#setCategoryLogo(this.isoLogo, this.isoLogoWrap, category.isoLogo);

        this.resultSource.textContent = category.source || '';
    }

    #setCategoryLogo(img, wrap, url) {
        if (!img || !wrap) return;

        if (url) {
            img.src = url;
            wrap.hidden = false;
            return;
        }

        img.removeAttribute('src');
        wrap.hidden = true;
    }

    #goResultsPage(delta) {
        const totalPages = Math.max(1, Math.ceil(this.#resultsBuffer.length / this.#resultsPageSize));
        this.#resultsPageIndex = Math.min(totalPages - 1, Math.max(0, this.#resultsPageIndex + delta));
        this.#renderResultsPage();
    }

    #normalizeTooltipSize(size) {
        const key = String(size || 'medium').toLowerCase();
        return key === 'small' || key === 'large' ? key : 'medium';
    }

    #applyTooltipSize(tooltip, size) {
        const content = tooltip?.querySelector('[slot="content"]');
        if (!content) return;

        const normalized = this.#normalizeTooltipSize(size);

        LabTests.#ALL_TOOLTIP_SIZE_CLASSES.forEach((className) => {
            content.classList.remove(className);
        });
        LabTests.#TOOLTIP_SIZE_CLASSES[normalized].forEach((className) => {
            content.classList.add(className);
        });
        content.dataset.tooltipSize = normalized;
    }

    #setupResultStatusTooltip(wrapper, result, idSuffix) {
        const cell = wrapper.querySelector('[data-lab-test-result-status-cell]');
        if (!cell) return;

        const tooltip = cell.querySelector('ui-tooltip');
        const tooltipText = result?.tooltip ? String(result.tooltip).trim() : '';

        if (!tooltipText) {
            const iconHtml = tooltip?.querySelector('[slot="trigger"]')?.innerHTML;

            cell.innerHTML = iconHtml ? `<span class="inline-flex" aria-hidden="true">${iconHtml}</span>` : '';

            return;
        }

        if (!tooltip) return;

        const contentP = tooltip.querySelector('[slot="content"] p');
        const contentSlot = tooltip.querySelector('[slot="content"]');
        const trigger = tooltip.querySelector('[slot="trigger"]');
        const id = `lab-result-status-${idSuffix}`;

        if (contentP) contentP.textContent = tooltipText;
        if (contentSlot) contentSlot.id = id;
        if (trigger) trigger.setAttribute('aria-controls', id);
        this.#applyTooltipSize(tooltip, result.tooltip_size);
    }

    #renderResultsPage() {
        const results = this.#resultsBuffer;
        const pageSize = this.#resultsPageSize;
        const totalPages = Math.max(1, Math.ceil(results.length / pageSize));

        if (this.#resultsPageIndex > totalPages - 1) {
            this.#resultsPageIndex = Math.max(0, totalPages - 1);
        }

        const start = this.#resultsPageIndex * pageSize;
        const slice = results.slice(start, start + pageSize);

        const existing = this.resultsContainer.querySelectorAll('[data-lab-test-rendered-result]');
        existing.forEach((item) => item.remove());

        slice.forEach((result, index) => {
            const clone = this.resultTemplate.content.cloneNode(true);
            const wrapper = clone.firstElementChild;

            wrapper.setAttribute('data-lab-test-rendered-result', '');
            clone.querySelector('[data-lab-test-result-name]').textContent = result.name;
            clone.querySelector('[data-lab-test-result-value]').textContent = result.value;

            this.#setupResultStatusTooltip(wrapper, result, `${this.currentCategory}-${start + index}`);

            this.resultsContainer.append(clone);
        });

        if (!this.resultsPagination) return;

        if (results.length <= pageSize) {
            this.resultsPagination.hidden = true;
            return;
        }

        this.resultsPagination.hidden = false;
        if (this.resultsPageCurrentEl) {
            this.resultsPageCurrentEl.textContent = String(this.#resultsPageIndex + 1);
        }
        if (this.resultsPageTotalEl) {
            this.resultsPageTotalEl.textContent = String(totalPages);
        }
        if (this.resultsPrevBtn) {
            this.resultsPrevBtn.disabled = this.#resultsPageIndex === 0;
        }
        if (this.resultsNextBtn) {
            this.resultsNextBtn.disabled = this.#resultsPageIndex >= totalPages - 1;
        }
    }

    #renderUnderstanding(items) {
        const validItems = (items || []).filter(
            (item) => String(item?.label ?? '').trim() || String(item?.description ?? '').trim()
        );

        if (this.understandingSection) {
            this.understandingSection.hidden = validItems.length === 0;
        }

        const existing = this.understandingContainer.querySelectorAll('[data-lab-test-rendered-understanding]');

        existing.forEach((item) => item.remove());

        validItems.forEach((item) => {
            const clone = this.understandingTemplate.content.cloneNode(true);

            const wrapper = clone.firstElementChild;

            wrapper.setAttribute('data-lab-test-rendered-understanding', '');

            const paragraphs = clone.querySelectorAll('p');

            paragraphs[0].textContent = item.label;
            paragraphs[1].textContent = item.description;

            this.understandingContainer.append(clone);
        });
    }
}

customElements.define('lab-tests', LabTests);